import { useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { getCalendarEvents } from "../api/calendar";
import CalendarView from "../components/calendar/CalendarView";
import { useAuth } from "../contexts/AuthContext";

const parse = (i) => {
  i.start = moment(i.start.human, "DD/MM/YYYY HH:mm");
  i.end = moment(i.end.human, "DD/MM/YYYY HH:mm");
  return i;
};

// Event times are the user's timezone wall clock parsed as browser-local, so
// `now` is too, or "now", "up next" and the now line drift when they differ
const wallClockNow = (tz) => {
  if (!tz || !moment.tz.zone(tz)) return moment();
  const f = "YYYY-MM-DD HH:mm:ss";
  return moment(moment().tz(tz).format(f), f);
};

// Ticks every minute for the now line and "up next"
function useNow(tz) {
  const [now, setNow] = useState(() => wallClockNow(tz));
  useEffect(() => {
    setNow(wallClockNow(tz));
    const id = setInterval(() => setNow(wallClockNow(tz)), 60000);
    return () => clearInterval(id);
  }, [tz]);
  return now;
}

function Calendar() {
  const { apiUser } = useAuth();
  const now = useNow(apiUser?.timezone);
  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ["calendar-events"],
    queryFn: async () => {
      const res = await getCalendarEvents();
      return {
        week: res.data.data.week.map(parse),
        upcoming: res.data.data.upcoming.map(parse),
      };
    },
    keepPreviousData: true,
    cacheTime: 3600000, // one hour
    staleTime: 60000, // 1 mins
  });

  return (
    <CalendarView
      week={data?.week}
      upcoming={data?.upcoming}
      isLoading={isLoading}
      error={!data && error}
      onRetry={() => refetch()}
      now={now}
    />
  );
}

export default Calendar;
